import { usePostMSSchedules, usePutMSSchedules } from "queries/meetingSchedule";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import FirstStepSchedule from "../Step/FirstStepSchedule";
import SecondStepSchedule from "../Step/SecondStepSchedule";
import ThirdStepSchedule from "../Step/ThirdStepSchedule";
import FourthStepSchedule from "../Step/FourthStepSchedule";
import { Modal, Steps } from "antd";
import dayjs from "dayjs";
import { cleanBlankValue } from "utils/helper";
import { useTranslation } from "react-i18next";

const CreateSchedule = ({
  open,
  dataDetail = null,
  onClose,
  refetch,
  showSuccessCreate,
  onCloseDetail,
  inhouseUsers,
}) => {
  const { t } = useTranslation();
  const handleCancel = () => {
    onClose(null);
  };
  return (
    <>
      <Modal
        title={
          open?.mode === "edit"
            ? t("meeting_management.edit_schedule")
            : t("meeting_management.add_schedule")
        }
        open={open}
        onCancel={handleCancel}
        footer={null}
        width={1000}
        maskClosable={false}
        destroyOnHidden
      >
        <FormCreateSchedule
          dataDetail={dataDetail}
          refetch={refetch}
          onClose={onClose}
          showSuccessCreate={showSuccessCreate}
          onCloseDetail={onCloseDetail}
          open={open}
          inhouseUsers={inhouseUsers}
        />
      </Modal>
    </>
  );
};

export default CreateSchedule;

const FormCreateSchedule = ({
  dataDetail,
  refetch,
  onClose,
  showSuccessCreate,
  onCloseDetail,
  open,
  inhouseUsers,
}) => {
  const { t } = useTranslation();
  const createMutation = usePostMSSchedules();
  const updateMutation = usePutMSSchedules();
  const [current, setCurrent] = useState(0);

  const [dataForm, setDataForm] = useState({
    start_time: null,
    end_time: null,
    admin_group_id: null,
    dataGroup: null,
    schedule_admin_id: null,
    modeForm: null,
    contact_id: null,
    contact: null,
    groups: null,
    note: null,
    status_id: null,
    tag_ids: [],
    date: null,
    isDoneInitiateData: false,
    isFixedAdmin: false,
  });
  // step handler
  useEffect(() => {
    if (open && open?.step !== 0 && current === 0) {
      setCurrent(open?.step);
    }
  }, [dataDetail, current, open]);

  // dataDetail handler — hanya sekali (saat modal dibuka). Jika dijalankan ulang
  // tiap `dataDetail` (objek inline) berubah karena AllSchedule re-render, semua
  // progress (mis. modeForm "auto") akan ke-reset.
  const initializedRef = useRef(false);
  useEffect(() => {
    if (dataDetail && !initializedRef.current) {
      initializedRef.current = true;
      setDataForm((prev) => ({
        ...prev,
        start_time: dataDetail.start_time
          ? dayjs(dataDetail.start_time, "HH:mm:ss").format("HH:mm")
          : null,
        end_time: dataDetail.end_time
          ? dayjs(dataDetail.end_time, "HH:mm:ss").format("HH:mm")
          : null,
        admin_group_id: dataDetail.admin_group_id || null,
        dataGroup: dataDetail.dataGroup || null,
        schedule_admin_id: dataDetail.schedule_admin_id || null,
        modeForm: dataDetail.modeForm || null,
        contact_id: dataDetail.contact_id || null,
        contact: dataDetail.contact || null,
        groups: dataDetail.groups || null,
        note: dataDetail.note || null,
        status_id: dataDetail.status_id || null,
        tag_ids: Array.isArray(dataDetail.tags)
          ? dataDetail.tags.map((tg) => tg.id)
          : dataDetail.tag_ids || [],
        date: dataDetail.date || null,
        isDoneInitiateData: true,
        isFixedAdmin: dataDetail.schedule_admin_id ? true : false,
      }));
    }
  }, [dataDetail]);

  const onSubmitData = useCallback(
    (payload) => {
      const payloadData = cleanBlankValue({
        ...payload,
      });
      // field bantu UI (Auto) — tidak dikirim ke API
      delete payloadData.auto_agent_name;
      delete payloadData.auto_reasoning;
      delete payloadData.auto_state;
      if (dataDetail?.id) {
        payloadData.id = dataDetail.id;
      }
      if (open?.mode === "edit") {
        updateMutation.mutate(payloadData, {
          onSuccess: (response) => {
            if (response.data.success) {
              refetch();
              onClose(null);
              showSuccessCreate({
                ...response.data.data,
                isEdit: true,
              });
              onCloseDetail();
              // setCurrent(0);
            }
          },
          onError: (error) => {
            console.log(error);
          },
        });
      } else {
        createMutation.mutate(payloadData, {
          onSuccess: (response) => {
            if (response.data.success) {
              refetch();
              onClose(null);
              showSuccessCreate(response.data.data);
              // setCurrent(0);
            }
          },
          onError: (error) => {
            console.log(error);
          },
        });
      }
    },
    [
      dataDetail,
      refetch,
      onClose,
      showSuccessCreate,
      open,
      createMutation,
      updateMutation,
      onCloseDetail,
    ]
  );

  const next = useCallback(() => {
    current !== open.steps.length - 1 && setCurrent(current + 1);
  }, [current, open.steps.length]);

  const prev = useCallback(() => {
    if (current === 2 && dataForm.dataGroup.auto_assign === 1) {
      setCurrent(0);
      return;
    }
    setCurrent(current - 1);
  }, [current, dataForm]);

  const onChangeDataForm = useCallback(
    (newData, last = false) => {
      if (!last) {
        setDataForm((prev) => ({
          ...prev,
          ...newData,
        }));
        next();
      } else {
        onSubmitData({
          ...dataForm,
          ...newData,
        });
      }
    },
    [next, dataForm, onSubmitData]
  );

  const steps = useMemo(() => {
    const result = [];
    if (open?.steps?.includes("group")) {
      result.push({
        title: t("meeting_schedule.group_admin"),
        content: (
          <FirstStepSchedule
            onChangeDataForm={onChangeDataForm}
            dataForm={dataForm}
          />
        ),
      });
    }
    if (open?.steps?.includes("admin")) {
      result.push({
        title: t("meeting_management.admin"),
        content: (
          <SecondStepSchedule
            skip={next}
            prev={prev}
            onChangeDataForm={onChangeDataForm}
            dataForm={dataForm}
          />
        ),
      });
    }
    if (open?.steps?.includes("schedule")) {
      result.push({
        title: t("meeting_management.schedule"),
        content: (
          <ThirdStepSchedule
            prev={prev}
            isEdit={open.mode === "edit"}
            onChangeDataForm={onChangeDataForm}
            dataForm={dataForm}
          />
        ),
      });
    }
    if (open?.steps?.includes("detail")) {
      const stepsOnlyGroupAndDetail =
        open?.steps?.length === 2 &&
        open.steps.includes("group") &&
        open.steps.includes("detail");
      const singleGroupSkip =
        stepsOnlyGroupAndDetail && dataForm.groups?.length === 1;
      result.push({
        title: t("meeting_management.detil"),
        content: (
          <FourthStepSchedule
            prev={prev}
            disabledPrev={singleGroupSkip}
            createLoading={createMutation.isPending || updateMutation.isPending}
            onChangeDataForm={onChangeDataForm}
            dataForm={dataForm}
            inhouseUsers={inhouseUsers}
          />
        ),
      });
    }
    return result;
  }, [
    t,
    createMutation,
    updateMutation,
    next,
    open,
    prev,
    onChangeDataForm,
    dataForm,
    inhouseUsers,
  ]);

  const items = steps.map((item) => ({
    key: item.title,
    title: item.title,
  }));
  return (
    <>
      <Steps current={current} items={items} />
      <div className="py-5 ">{steps[current]?.content}</div>
    </>
  );
};
